import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { useSwitcherClientCore } from "./useSwitcherClient";
import {
    BroadcastResponse,
    BroadcastsPagedResponse,
    VideoStatusType,
    SortType,
    VideoFilterType
} from "@switcherstudio/api-core-client";
import { checkBroadcastType } from "helpers/broadcastHelpers";
import { refetchParams } from "components/generic-multiselect/types";

export interface useGetPagedBroadcastByFilterOptions {
    videoStatusType?: VideoStatusType;
    preFetch?: () => void;
    onSuccess?: (
        data: BroadcastsPagedResponse,
        transformedData: unknown
    ) => Promise<void> | void;
    /** Number of items per page. Defaults to 0 (retrieves all videos on first page). */
    itemsPerPage?: number;
    sortType?: SortType;
    sortOrder?: "Asc" | "Desc";
    /**
     * An array of ids of players whose broadcasts should be excluded from
     * response - for use in the AddVideosModal to exclude videos
     * already in the collection.
     */
    excludedPlayerIds?: string[];
}

export const useGetPagedBroadcastByFilter = ({
    videoStatusType = "All",
    preFetch,
    onSuccess,
    itemsPerPage = 0,
    sortType = "Date",
    sortOrder = "Desc",
    excludedPlayerIds = []
}: useGetPagedBroadcastByFilterOptions) => {
    // Redux state
    const {
        userInfo: {
            MetaData: {
                Details: { ProjectId }
            }
        }
    } = useSelector((state: RootState) => state.user);

    // Local state
    const [totalBroadcasts, setTotalBroadcasts] = useState<number>(undefined);
    const [totalPages, setTotalPages] = useState<number>(undefined);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [nextPageLoading, setNextPageLoading] = useState<boolean>(false);
    const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
    const [allBroadcasts, setAllBroadcasts] = useState<BroadcastResponse[]>([]);

    // Search/Sort/Filter states
    const [sortTypeState, setSortTypeState] = useState<SortType>(sortType);
    const [sortOrderState, setSortOrderState] = useState<"Asc" | "Desc">(
        sortOrder
    );
    const [tagIds, setTagIds] = useState<string[]>([]);
    const [activeFilter, setActiveFilter] = useState<VideoFilterType>("All");
    const [textQuery, setTextQuery] = useState<string>("");

    const setPaginationInfo = useCallback(
        (res: BroadcastsPagedResponse) => {
            if (
                totalBroadcasts === undefined &&
                res?.TotalRecords !== totalBroadcasts
            ) {
                setTotalBroadcasts(res?.TotalRecords ?? 0);
            }

            if (totalPages === undefined && res?.TotalPages !== totalPages) {
                setTotalPages(res?.TotalPages ?? 0);
            }
        },
        [totalBroadcasts, totalPages]
    );

    // Fetch data
    const {
        dispatchApiRequest: refetchVideos,
        loading,
        error
    } = useSwitcherClientCore(
        (client) => client.broadcasts_GetPagedBroadcastsByFilter,
        {
            suppressErrorBoundary: true,
            args: [
                ProjectId,
                currentPage,
                itemsPerPage,
                "Project",
                videoStatusType,
                textQuery,
                tagIds,
                activeFilter,
                sortTypeState,
                sortOrderState,
                excludedPlayerIds
            ],
            requestImmediately: true,
            preFetch,
            onSuccess: (res) => {
                if (itemsPerPage === 0) {
                    setAllBroadcasts(res?.Data?.[0]?.Broadcasts);
                } else {
                    setAllBroadcasts((prev) => [
                        // Only include previous items if this is not the first page
                        ...(currentPage > 1 ? prev : []),
                        ...(res?.Data?.[0]?.Broadcasts || [])
                    ]);
                }

                setPaginationInfo(res);
                setIsFirstLoad(false);
                setNextPageLoading(false);
                onSuccess?.(res, res?.Data?.[0]?.Broadcasts);
            }
        }
    );

    const fetchBroadcastsWithNewParams = useCallback(
        (
            { sortType, sortOrder, filter, tags, text }: refetchParams,
            resetFirstLoad = true,
            /**
             * When included and true, will short circuit function without resetting
             * data, filters/searches/sorts, and pagination info when changing
             * tabs in the AddVideosModal. Meant for exclusive use
             * in the debounced text input useEffect.
             */
            fromDebouncedTextInputEffect = false
        ): boolean => {
            if (fromDebouncedTextInputEffect && text === textQuery) {
                return false;
            }

            setIsFirstLoad(resetFirstLoad);

            if (sortType) setSortTypeState(sortType);
            if (sortOrder) setSortOrderState(sortOrder);
            if (tags) setTagIds(tags);
            if (filter) setActiveFilter(filter);
            if (typeof text === "string") {
                setTextQuery(text);
            }

            // Reset pagination info
            setTotalBroadcasts(undefined);
            setTotalPages(undefined);
            setCurrentPage(1); // resetting current page refetches data (now with activeFilter)

            return true;
        },
        [textQuery]
    );

    const { firstLoading, reloading } = useMemo(
        () => ({
            firstLoading: loading && isFirstLoad,
            reloading: loading && !isFirstLoad
        }),
        [loading, isFirstLoad]
    );

    const categorizeBroadcasts = (
        allBroadcasts: BroadcastResponse[]
    ): {
        [key: string]: BroadcastResponse[];
    } => {
        const existingBroadcasts = [];
        const scheduledBroadcasts = [];
        const upcomingLivestreams = [];
        const unpublishedBroadcasts = [];

        if (!allBroadcasts || !allBroadcasts.length) {
            return {
                existingBroadcasts,
                scheduledBroadcasts,
                upcomingLivestreams,
                unpublishedBroadcasts
            };
        }

        for (const broadcast of allBroadcasts) {
            if (!!broadcast.Details.InputEffectiveDeletionDate) {
                continue;
            }

            const {
                isPremiere,
                isScheduledLivestream,
                isExistingContent,
                isUnpublished
            } = checkBroadcastType(broadcast);

            if (isScheduledLivestream) {
                upcomingLivestreams.push(broadcast);
            } else if (isPremiere) {
                scheduledBroadcasts.push(broadcast);
            } else if (isExistingContent) {
                existingBroadcasts.push(broadcast);
            } else if (isUnpublished) {
                unpublishedBroadcasts.push(broadcast);
            }
        }

        return {
            existingBroadcasts,
            scheduledBroadcasts,
            upcomingLivestreams,
            unpublishedBroadcasts
        };
    };

    const {
        existingBroadcasts,
        scheduledBroadcasts,
        upcomingLivestreams,
        unpublishedBroadcasts
    } = useMemo((): {
        [key: string]: BroadcastResponse[];
    } => {
        return categorizeBroadcasts(allBroadcasts);
    }, [allBroadcasts]);

    const getNextPage = useCallback(() => {
        if (loading || !totalPages || currentPage + 1 > totalPages) {
            return;
        }

        setNextPageLoading(true);
        setCurrentPage(currentPage + 1);
    }, [loading, currentPage, totalPages]);

    const refetchFromPageOne = useCallback(
        (newParams?: Parameters<typeof refetchVideos>[0]) => {
            setTotalBroadcasts(undefined);
            setTotalPages(undefined);
            setAllBroadcasts([]);
            setIsFirstLoad(true);

            setCurrentPage(1);
            newParams ? refetchVideos(newParams) : refetchVideos();
        },
        [refetchVideos]
    );

    const allLoaded = useMemo(() => {
        // totalPages === 0 when itemsPerPage is 0
        return totalPages === 0 || totalPages === currentPage;
    }, [totalPages, currentPage]);

    return {
        categorizeBroadcasts,
        getNextPage,
        nextPageLoading,
        existingBroadcasts,
        scheduledBroadcasts,
        upcomingLivestreams,
        unpublishedBroadcasts,
        firstLoading,
        reloading,
        refetchVideos,
        refetchFromPageOne,
        error,
        fetchBroadcastsWithNewParams,
        allLoaded
    };
};
